import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

import { useAppDispatch } from 'store/hooks';
import { FjSideNav, FjSideNavItem } from 'components/primitives';
import InstitutionLicenseIcon from 'icons/institution-license';
import IndividualLicenseIcon from 'icons/individual-license';
import SettingsIcon from 'icons/settings';
import { TOOL_BUTTON_NAMES } from 'constants/super-admin';
import { selectActiveTool } from 'selectors/super-admin';
import { SuperAdminActions } from 'actions';
import UsersIcon from 'icons/users';

const AdminToolArea = (): React.ReactElement => {
  // `selectActiveTool` reads from untyped redux state and resolves as `unknown`;
  // at runtime it is the active tool button id (a string).
  const selectedTool = useSelector(selectActiveTool) as string | undefined;
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const handleTabChange = (tabId: string) => dispatch(SuperAdminActions.selectTool(tabId));

  // FjSideNav consumes `style` as a custom side-nav theme; the app DefaultTheme
  // supplies `colors` at runtime via the surrounding ThemeProvider, so cast to
  // the expected prop shape rather than duplicating that value here.
  const sideNavStyle = {
    primaryColor: theme.colors.selected,
    backgroundColor: theme.colors.toolbar,
    width: theme.grid.units(4),
    titleFontSize: theme.fontSizes.sidebarTitle.default,
    titleHeight: '70px',
    iconBoxHeight: theme.grid.units(4),
    iconSize: '40px',
  } as React.ComponentProps<typeof FjSideNav>['style'];

  return (
    <FjSideNav
      activeTab={selectedTool}
      style={sideNavStyle}
      onTabSelected={(id: string) => handleTabChange(id)}
    >
      <FjSideNavItem
        title="Institution License"
        id={TOOL_BUTTON_NAMES.INSTITUTION_LICENSE}
        icon={<InstitutionLicenseIcon style={theme.colors.sideNavIcons} />}
        titleFontSizeOverride={theme.fontSizes.sidebarTitle.small}
        data-testid="institution-tool-button"
      />
      <FjSideNavItem
        title="Individual License"
        id={TOOL_BUTTON_NAMES.INDIVIDUAL_LICENSE}
        icon={<IndividualLicenseIcon style={theme.colors.sideNavIcons} />}
        titleFontSizeOverride={theme.fontSizes.sidebarTitle.small}
        data-testid="individual-tool-button"
      />
      <FjSideNavItem
        title="Users"
        id={TOOL_BUTTON_NAMES.USERS}
        icon={<UsersIcon style={theme.colors.sideNavIcons} />}
        data-testid="users-tool-button"
      />
      <FjSideNavItem
        title="Product Version"
        id={TOOL_BUTTON_NAMES.PRODUCT_VERSION}
        icon={<SettingsIcon style={theme.colors.sideNavIcons} />}
        titleFontSizeOverride={theme.fontSizes.sidebarTitle.small}
        data-testid="product-version-tool-button"
      />
    </FjSideNav>
  );
};

export default AdminToolArea;
